import { getCompanyHasPricesWithoutTax } from '@mahawi/eshop-common/dist/src/get-company-has-prices-without-tax';
import { getVatCountryIsoCode } from '@mahawi/eshop-common/dist/src/get-vat-country-iso-code';
import {
  EEventContextAction,
  EEventType,
  type ICountry,
  type IOrderValidationFormErrors,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';

import { setFields } from '../../reducers/basket/reducer';
import { eventSend } from '../../reducers/event/reducer';
import { changeCountryType } from '../../reducers/user/reducer';
import { type IOrderFormFields } from './order-form';

// Pre-compiled regex for better performance
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Constants for validation
const MIN_LENGTH = {
  NAME: 2,
  COMPANY_ID: 2,
  EMAIL: 6,
  PHONE: 8,
  STREET: 2,
  STREET_NUMBER: 1,
  CITY: 2,
  ZIP: 2,
  COUNTRY_CODE: 3,
} as const;

// Pre-create empty errors object to avoid recreation
const createEmptyErrors = (): IOrderValidationFormErrors => ({
  billingAddress: {
    name: '',
    surname: '',
    email: '',
    phone: '',
    street: '',
    streetNumber: '',
    city: '',
    zip: '',
    countryTypeIsoCode: '',
    companyName: '',
    companyId: '',
    companyTaxId: '',
  },
  deliveryAddress: {
    name: '',
    surname: '',
    email: '',
    phone: '',
    street: '',
    streetNumber: '',
    city: '',
    zip: '',
    countryTypeIsoCode: '',
  },
});

// Validation helper functions for better readability and reusability
const validateField = (
  value: string | undefined,
  minLength: number,
  errorMessage: string,
): string => {
  return !value || value.length < minLength ? errorMessage : '';
};

const validateEmail = (
  email: string | undefined,
  t: (key: string) => string,
): string => {
  if (!email || email.length < MIN_LENGTH.EMAIL || !EMAIL_PATTERN.test(email)) {
    return t('basket.emailIsNotValid');
  }
  return '';
};

const validateCountry = (
  countryCode: string | undefined,
  t: (key: string) => string,
): string => {
  if (
    !countryCode ||
    countryCode.length < MIN_LENGTH.COUNTRY_CODE ||
    countryCode === '...'
  ) {
    return t('basket.countryIsNotSet');
  }
  return '';
};

// Memoized country lookup
const findCountryByCode = (
  countries: ICountry[],
  isoCode: string,
): ICountry | undefined => {
  return countries.find(
    (country: ICountry): boolean => country.isoCode === isoCode,
  );
};

interface ValidationParams {
  values: IOrderFormFields;
  dispatch: Dispatch;
  configCountryTypeIsoCode: string | null | undefined;
  countries: ICountry[];
  deliveryNote: string;
  isForCompany: boolean;
  setCompanyHasPricesWithoutTax: (value: boolean) => void;
  setBeginCheckout: (value: boolean) => void;
  t: (key: string) => string;
}

export const orderValidation = ({
  values,
  dispatch,
  setBeginCheckout,
  setCompanyHasPricesWithoutTax,
  isForCompany,
  t,
  countries,
  deliveryNote,
}: ValidationParams) => {
  const {
    billingAddress,
    deliveryAddress,
    deliveryAddressIsDifferent,
    heurekaCZShopCertification,
    termsAndConditions,
    discount,
  } = values;

  setBeginCheckout(true);

  const errors: IOrderValidationFormErrors = createEmptyErrors();
  let isValid: boolean = true;

  // Company validation
  if (isForCompany) {
    const companyNameError: string = validateField(
      billingAddress.companyName,
      MIN_LENGTH.NAME,
      t('basket.companyNameIsTooShort'),
    );
    if (companyNameError) {
      isValid = false;
      errors.billingAddress.companyName = companyNameError;
    }

    const companyIdError: string = validateField(
      billingAddress.companyId,
      MIN_LENGTH.COMPANY_ID,
      t('basket.companyIdIsTooShort'),
    );
    if (companyIdError) {
      isValid = false;
      errors.billingAddress.companyId = companyIdError;
    }

    if (billingAddress.companyTaxId) {
      const companyTaxIdError: string = validateField(
        billingAddress.companyTaxId,
        MIN_LENGTH.NAME,
        t('basket.companyTaxIdIsTooShort'),
      );
      if (companyTaxIdError) {
        isValid = false;
        errors.billingAddress.companyTaxId = companyTaxIdError;
      }
    }
  }

  // Billing address validation
  const billingValidations = [
    {
      field: 'name',
      value: billingAddress.name,
      minLength: MIN_LENGTH.NAME,
      key: 'basket.nameIsTooShort',
    },
    {
      field: 'surname',
      value: billingAddress.surname,
      minLength: MIN_LENGTH.NAME,
      key: 'basket.surnameIsTooShort',
    },
    {
      field: 'phone',
      value: billingAddress.phone,
      minLength: MIN_LENGTH.PHONE,
      key: 'basket.phoneIsNotValid',
    },
    {
      field: 'street',
      value: billingAddress.street,
      minLength: MIN_LENGTH.STREET,
      key: 'basket.streetIsTooShort',
    },
    {
      field: 'streetNumber',
      value: billingAddress.streetNumber,
      minLength: MIN_LENGTH.STREET_NUMBER,
      key: 'basket.streetNumberIsTooShort',
    },
    {
      field: 'city',
      value: billingAddress.city,
      minLength: MIN_LENGTH.CITY,
      key: 'basket.cityIsTooShort',
    },
    {
      field: 'zip',
      value: billingAddress.zip,
      minLength: MIN_LENGTH.ZIP,
      key: 'basket.zipIsTooShort',
    },
  ] as const;

  for (const validation of billingValidations) {
    const error: string = validateField(
      validation.value,
      validation.minLength,
      t(validation.key),
    );
    if (error) {
      isValid = false;
      errors.billingAddress[validation.field] = error;
    }
  }

  // Email validation for billing
  const billingEmailError: string = validateEmail(billingAddress.email, t);
  if (billingEmailError) {
    isValid = false;
    errors.billingAddress.email = billingEmailError;
  }

  // Country validation for billing
  const billingCountryError: string = validateCountry(
    billingAddress.countryTypeIsoCode,
    t,
  );
  if (billingCountryError) {
    isValid = false;
    errors.billingAddress.countryTypeIsoCode = billingCountryError;
  }

  // Delivery address validation (if different)
  if (deliveryAddressIsDifferent) {
    const deliveryValidations = [
      {
        field: 'name',
        value: deliveryAddress.name,
        minLength: MIN_LENGTH.NAME,
        key: 'basket.nameIsTooShort',
      },
      {
        field: 'surname',
        value: deliveryAddress.surname,
        minLength: MIN_LENGTH.NAME,
        key: 'basket.surnameIsTooShort',
      },
      {
        field: 'phone',
        value: deliveryAddress.phone,
        minLength: MIN_LENGTH.PHONE,
        key: 'basket.phoneIsNotValid',
      },
      {
        field: 'street',
        value: deliveryAddress.street,
        minLength: MIN_LENGTH.STREET,
        key: 'basket.streetIsTooShort',
      },
      {
        field: 'streetNumber',
        value: deliveryAddress.streetNumber,
        minLength: MIN_LENGTH.STREET_NUMBER,
        key: 'basket.streetNumberIsTooShort',
      },
      {
        field: 'city',
        value: deliveryAddress.city,
        minLength: MIN_LENGTH.CITY,
        key: 'basket.cityIsTooShort',
      },
      {
        field: 'zip',
        value: deliveryAddress.zip,
        minLength: MIN_LENGTH.ZIP,
        key: 'basket.zipIsTooShort',
      },
    ] as const;

    for (const validation of deliveryValidations) {
      const error: string = validateField(
        validation.value,
        validation.minLength,
        t(validation.key),
      );
      if (error) {
        isValid = false;
        errors.deliveryAddress[validation.field] = error;
      }
    }

    const deliveryEmailError: string = validateEmail(deliveryAddress.email, t);
    if (deliveryEmailError) {
      isValid = false;
      errors.deliveryAddress.email = deliveryEmailError;
    }

    const deliveryCountryError: string = validateCountry(
      deliveryAddress.countryTypeIsoCode,
      t,
    );
    if (deliveryCountryError) {
      isValid = false;
      errors.deliveryAddress.countryTypeIsoCode = deliveryCountryError;
    }
  }

  const vatCountryIsoCode: string = getVatCountryIsoCode({
    billingAddress,
    deliveryAddress,
    deliveryAddressIsDifferent,
    excludedCountryIsoCode: 'CZE',
  });

  const vatCountryType: ICountry | undefined = findCountryByCode(
    countries,
    vatCountryIsoCode,
  );

  if (vatCountryType) {
    dispatch(changeCountryType({ countryType: vatCountryType }));
  }

  // Update state (batch these operations)
  dispatch(
    setFields({
      billingAddress,
      deliveryAddress,
      termsAndConditions,
      deliveryAddressIsDifferent,
      heurekaCZShopCertification,
      discount,
      isForCompany,
      deliveryNote,
    }),
  );

  // Calculate company prices (memoize this if possible)
  const hasPricesWithoutTax: boolean = getCompanyHasPricesWithoutTax({
    isForCompany,
    billingAddress,
    deliveryAddress,
    deliveryAddressIsDifferent,
    excludedCountryIsoCode: 'CZE',
  });
  setCompanyHasPricesWithoutTax(hasPricesWithoutTax);

  // Send validation event only on error
  if (!isValid) {
    dispatch(
      eventSend({
        type: EEventType.ORDER_VALIDATION,
        action: EEventContextAction.VALIDATE,
        context: {
          isValid: false,
          errors,
        },
      }),
    );
  }

  return isValid ? {} : errors;
};
